import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import SupportModal from "./SupportModal";
import { HandHeart, Heart, Coffee } from 'lucide-react';

const PayPalIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const TikTokIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className="text-slate-700"
  >
    <path
      fill="currentColor"
      d="M14 3c.6 1.8 2 3.3 3.8 3.9 1 .3 2.1.4 3.2.3v2.8c-2.4.1-4.7-.7-6.6-2.2v7.2a5.8 5.8 0 1 1-5.8-5.8c.4 0 .7 0 1.1.1v3a2.7 2.7 0 1 0 1.6 2.4V3h2.7z"
    />
  </svg>
);

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [showSupportModal, setShowSupportModal] = useState(false);

  const handleSuggestionsClick = () => navigate('/suggestions');
  const handleLegalClick = () => navigate('/?page=legal');

  return (
    <footer className="mt-12 border-t border-slate-100">
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          
          {/* Logo and Team */}
          <div className="flex items-center gap-3">
            {/* <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-rose-pastel-400 to-lavender-400"></div> */}
            <div className="text-sm">
              <img src="/images/markersync-logo-transparent.png" alt="MarkerSync Logo" className="w-10 h-10 rounded-lg inline-block" />
              <span className="font-medium text-slate-600">MarkerSync</span>
              <span className="text-slate-400 ml-2">{t('footer.byTeam')}</span>
            </div>
          </div>

          {/* Contact Links */}
          <div className="flex items-center gap-2 text-xs">
            <a 
              href="mailto:markersync@gmail.com"
              className="text-slate-500 hover:text-slate-700 transition-colors"
            >
              markersync@gmail.com
            </a>
            <span className="text-slate-300">•</span>
            <a 
              href="https://www.tiktok.com/@markersync_"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-700 transition-colors flex gap-1"
            >
              <TikTokIcon size={16} /> @markersync_
            </a>
            <span className="text-slate-300">•</span>
            <button
              onClick={handleSuggestionsClick}
              className="text-orange-600 hover:text-orange-700 transition-colors underline"
            >
              {t('footer.suggestions')}
            </button>
          </div>
        </div>
        
        {/* Support Section - Delicate */}
        <div className="mt-2 py-6 border-t border-slate-100/50 bg-gradient-to-br from-slate-50/30 to-transparent rounded-lg">
          <div className="text-center space-y-5">
            {/* Gentle request */}
            <div className="max-w-sm mx-auto space-y-3">
              <div className="flex items-center justify-center gap-2 text-slate-600">
                <HandHeart size={30} className="text-rose-pastel-400" />
                <p className="text font-medium">
                  {t('support.title')}
                </p>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">
                {t('support.subtitle')}
                <Heart size={20} className="inline-block ml-1 pb-1 text-red-400" />
              </p>
            </div>
            
            {/* Support buttons */}
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href="https://buymeacoffee.com/markersync"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-yellow-200 text-yellow-700 hover:bg-yellow-50 hover:border-yellow-300 shadow-sm hover:shadow-md transition-all text-xs font-medium"
              >
                <Coffee size={14} />
                Buy Me a Coffee
              </a>
              <a
                href="https://paypal.me/markersync"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-sm hover:shadow-md transition-all text-xs font-medium"
              >
                <PayPalIcon size={14} />
                PayPal
              </a>
            </div>
            
            {/* Thank you note
            <p className="text-sm text-slate-400 italic">
              {t('support.thankYou')}
              <Stars size={16} className="inline-block ml-1 text-yellow-400" />
            </p> */}
          </div>
        </div>

      <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />

        {/* Disclaimer and Legal */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-center">
          <div className="flex items-center gap-2 text-xs">
            {/* <LucideAlertTriangle size={16} className="text-slate-400" /> */}
            <p className="text-xs text-slate-400">
              {t('footer.previewWarning')}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">© 2026 MarkerSync</span>
            <span className="text-slate-300">•</span>
            <a
              href="https://markersync.com/?page=legal"
              onClick={(e) => {
                e.preventDefault();
                handleLegalClick();
              }}
              className="text-slate-500 hover:text-slate-700 transition-colors underline"
            >
              {t('footer.legal')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
